import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Dimensions,
  ActivityIndicator,
  Image
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome, AntDesign } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function ProjectAnalyticsScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { project } = route.params;
  
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('month');
  const [companyLogo, setCompanyLogo] = useState(null);
  
  const screenWidth = Dimensions.get('window').width - 40;
  
  // Time range options
  const timeRangeOptions = [
    { id: 'week', label: 'Week' },
    { id: 'month', label: 'Month' },
    { id: 'quarter', label: 'Quarter' },
    { id: 'year', label: 'Year' },
  ];
  
  // Tab options
  const tabOptions = [
    { id: 'overview', label: 'Overview', icon: 'chart-bar' },
    { id: 'budget', label: 'Budget', icon: 'cash' },
    { id: 'tasks', label: 'Tasks', icon: 'checkbox-marked-circle-outline' },
    { id: 'team', label: 'Team', icon: 'account-group' },
  ];
  
  // Load company logo on mount
  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Load project data on component mount
  useEffect(() => {
    if (project) {
      setProjectData(project);
    }
  }, [project]);
  
  // Handle back button navigation
  const handleGoBack = () => {
    navigation.goBack();
  };
  
  // Generate mock data for charts
  const generateMockData = () => {
    // Progress data
    const progressData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          data: [20, 45, 58, 80, 99, 43],
          color: (opacity = 1) => `rgba(14, 78, 150, ${opacity})`,
          strokeWidth: 2
        }
      ],
      legend: ['Progress %']
    };
    
    // Budget data
    const budgetData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          data: [5000, 8000, 12000, 18000, 24000, 30000],
          color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`,
          strokeWidth: 2
        }
      ],
      legend: ['Budget Spent ($)']
    };
    
    // Task completion data
    const taskCompletionData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          data: [5, 12, 18, 25, 32, 40],
          color: (opacity = 1) => `rgba(245, 158, 11, ${opacity})`,
          strokeWidth: 2
        }
      ],
      legend: ['Tasks Completed']
    };
    
    // Task status data
    const taskStatusData = [
      {
        name: 'Completed',
        population: 40,
        color: '#10B981',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      },
      {
        name: 'In Progress',
        population: 25,
        color: '#F59E0B',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      },
      {
        name: 'Not Started',
        population: 35,
        color: '#64748B',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      }
    ];
    
    // Risk severity data
    const riskSeverityData = [
      {
        name: 'High',
        population: 15,
        color: '#EF4444',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      },
      {
        name: 'Medium',
        population: 25,
        color: '#F59E0B',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      },
      {
        name: 'Low',
        population: 60,
        color: '#10B981',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      }
    ];
    
    // Team allocation data
    const teamAllocationData = [
      {
        name: 'Development',
        population: 45,
        color: '#0e4e96',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      },
      {
        name: 'Design',
        population: 20,
        color: '#8B5CF6',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      },
      {
        name: 'QA',
        population: 15,
        color: '#F59E0B',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      },
      {
        name: 'Management',
        population: 20,
        color: '#10B981',
        legendFontColor: '#7F7F7F',
        legendFontSize: 12
      }
    ];
    
    return {
      progressData,
      budgetData,
      taskCompletionData,
      taskStatusData,
      riskSeverityData,
      teamAllocationData
    };
  };
  
  const chartData = generateMockData();
  
  // Chart configuration
  const chartConfig = {
    backgroundGradientFrom: '#fff',
    backgroundGradientTo: '#fff',
    decimalPlaces: 0,
    color: (opacity = 1) => `rgba(14, 78, 150, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(100, 116, 139, ${opacity})`,
    style: {
      borderRadius: 16
    },
    propsForDots: {
      r: '6',
      strokeWidth: '2',
      stroke: '#0e4e96'
    }
  };
  
  // Calculate project statistics
  const calculateProjectStats = () => {
    if (!projectData) return {};
    
    // Calculate days remaining
    const today = new Date();
    const endDate = new Date(projectData.endDate);
    const daysRemaining = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));
    
    // Calculate project duration in days
    const startDate = new Date(projectData.startDate);
    const totalDuration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    
    // Calculate progress percentage
    const elapsedDuration = Math.min(totalDuration, Math.ceil((today - startDate) / (1000 * 60 * 60 * 24)));
    const progressPercentage = Math.round((elapsedDuration / totalDuration) * 100);
    
    // Calculate milestone completion
    const totalMilestones = projectData.milestones ? projectData.milestones.length : 0;
    const completedMilestones = projectData.milestones ? 
      projectData.milestones.filter(m => m.status === 'Completed').length : 0;
    const milestonePercentage = totalMilestones > 0 ? 
      Math.round((completedMilestones / totalMilestones) * 100) : 0;
    
    // Calculate risk distribution
    const highRisks = projectData.risks ? 
      projectData.risks.filter(r => r.severity === 'High').length : 0;
    const mediumRisks = projectData.risks ? 
      projectData.risks.filter(r => r.severity === 'Medium').length : 0;
    const lowRisks = projectData.risks ? 
      projectData.risks.filter(r => r.severity === 'Low').length : 0;
    
    return {
      daysRemaining,
      totalDuration,
      progressPercentage,
      milestonePercentage,
      highRisks,
      mediumRisks,
      lowRisks,
      teamSize: projectData.team ? projectData.team.length : 0
    };
  };
  
  const projectStats = calculateProjectStats();
  
  // Render overview tab content
  const renderOverviewTab = () => {
    return (
      <View style={styles.tabContent}>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{projectStats.daysRemaining}</Text>
            <Text style={styles.statLabel}>Days Remaining</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{projectStats.progressPercentage}%</Text>
            <Text style={styles.statLabel}>Progress</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{projectStats.milestonePercentage}%</Text>
            <Text style={styles.statLabel}>Milestones</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{projectStats.teamSize}</Text>
            <Text style={styles.statLabel}>Team Members</Text>
          </View>
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Project Progress</Text>
          <LineChart
            data={chartData.progressData}
            width={screenWidth}
            height={220}
            chartConfig={chartConfig}
            bezier
            style={styles.chart}
          />
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Task Status</Text>
          <PieChart
            data={chartData.taskStatusData}
            width={screenWidth}
            height={220}
            chartConfig={chartConfig}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="15"
            style={styles.chart}
          />
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Risk Assessment</Text>
          <PieChart
            data={chartData.riskSeverityData}
            width={screenWidth}
            height={220}
            chartConfig={chartConfig}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="15"
            style={styles.chart}
          />
        </View>
      </View>
    );
  };
  
  // Render budget tab content
  const renderBudgetTab = () => {
    return (
      <View style={styles.tabContent}>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>$50,000</Text>
            <Text style={styles.statLabel}>Total Budget</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>$30,000</Text>
            <Text style={styles.statLabel}>Spent</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>$20,000</Text>
            <Text style={styles.statLabel}>Remaining</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>60%</Text>
            <Text style={styles.statLabel}>Utilization</Text>
          </View>
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Budget Utilization</Text>
          <LineChart
            data={chartData.budgetData}
            width={screenWidth}
            height={220}
            chartConfig={{
              ...chartConfig,
              color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`
            }}
            bezier
            style={styles.chart}
          />
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Budget Allocation</Text>
          <PieChart
            data={[
              {
                name: 'Labor',
                population: 65,
                color: '#0e4e96',
                legendFontColor: '#7F7F7F',
                legendFontSize: 12
              },
              {
                name: 'Materials',
                population: 20,
                color: '#F59E0B',
                legendFontColor: '#7F7F7F',
                legendFontSize: 12
              },
              {
                name: 'Equipment',
                population: 10,
                color: '#8B5CF6',
                legendFontColor: '#7F7F7F',
                legendFontSize: 12
              },
              {
                name: 'Other',
                population: 5,
                color: '#64748B',
                legendFontColor: '#7F7F7F',
                legendFontSize: 12
              }
            ]}
            width={screenWidth}
            height={220}
            chartConfig={chartConfig}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="15"
            style={styles.chart}
          />
        </View>
      </View>
    );
  };
  
  // Render tasks tab content
  const renderTasksTab = () => {
    return (
      <View style={styles.tabContent}>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>100</Text>
            <Text style={styles.statLabel}>Total Tasks</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>40</Text>
            <Text style={styles.statLabel}>Completed</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>25</Text>
            <Text style={styles.statLabel}>In Progress</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>35</Text>
            <Text style={styles.statLabel}>Not Started</Text>
          </View>
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Task Completion</Text>
          <LineChart
            data={chartData.taskCompletionData}
            width={screenWidth}
            height={220}
            chartConfig={{
              ...chartConfig,
              color: (opacity = 1) => `rgba(245, 158, 11, ${opacity})`
            }}
            bezier
            style={styles.chart}
          />
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Task Status</Text>
          <PieChart
            data={chartData.taskStatusData}
            width={screenWidth}
            height={220}
            chartConfig={chartConfig}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="15"
            style={styles.chart}
          />
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Task Priority Distribution</Text>
          <PieChart
            data={[
              {
                name: 'High',
                population: 30,
                color: '#EF4444',
                legendFontColor: '#7F7F7F',
                legendFontSize: 12
              },
              {
                name: 'Medium',
                population: 45,
                color: '#F59E0B',
                legendFontColor: '#7F7F7F',
                legendFontSize: 12
              },
              {
                name: 'Low',
                population: 25,
                color: '#10B981',
                legendFontColor: '#7F7F7F',
                legendFontSize: 12
              }
            ]}
            width={screenWidth}
            height={220}
            chartConfig={chartConfig}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="15"
            style={styles.chart}
          />
        </View>
      </View>
    );
  };
  
  // Render team tab content
  const renderTeamTab = () => {
    return (
      <View style={styles.tabContent}>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{projectStats.teamSize}</Text>
            <Text style={styles.statLabel}>Team Size</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>4</Text>
            <Text style={styles.statLabel}>Departments</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>85%</Text>
            <Text style={styles.statLabel}>Utilization</Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statValue}>92%</Text>
            <Text style={styles.statLabel}>Efficiency</Text>
          </View>
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Team Allocation</Text>
          <PieChart
            data={chartData.teamAllocationData}
            width={screenWidth}
            height={220}
            chartConfig={chartConfig}
            accessor="population"
            backgroundColor="transparent"
            paddingLeft="15"
            style={styles.chart}
          />
        </View>
        
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Team Performance</Text>
          <BarChart
            data={{
              labels: ['Dev', 'Design', 'QA', 'Mgmt'],
              datasets: [
                {
                  data: [90, 85, 95, 88]
                }
              ]
            }}
            width={screenWidth}
            height={220}
            chartConfig={{
              ...chartConfig,
              color: (opacity = 1) => `rgba(139, 92, 246, ${opacity})`
            }}
            style={styles.chart}
            verticalLabelRotation={0}
          />
        </View>
      </View>
    );
  };
  
  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'budget':
        return renderBudgetTab();
      case 'tasks':
        return renderTasksTab();
      case 'team':
        return renderTeamTab();
      default:
        return renderOverviewTab();
    }
  };
  
  if (!projectData) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading analytics...</Text>
        </View>
      </SafeAreaView>
    );
  }
  
  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      {/* Header with Back Button, Company Logo, and Title */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        
        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
          )}
          <Text style={styles.headerTitle}>Project Analytics</Text>
        </View>
        
        <View style={{ width: 30 }} />
      </View>
      
      {/* Project Title */}
      <View style={styles.projectHeader}>
        <Text style={styles.projectTitle}>{projectData.title}</Text>
      </View>
      
      {/* Time Range Selector */}
      <View style={styles.timeRangeContainer}>
        <Text style={styles.timeRangeLabel}>Time Range:</Text>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.timeRangeOptions}
        >
          {timeRangeOptions.map((option) => (
            <TouchableOpacity
              key={option.id}
              style={[
                styles.timeRangeOption,
                timeRange === option.id && styles.timeRangeOptionActive
              ]}
              onPress={() => setTimeRange(option.id)}
            >
              <Text
                style={[
                  styles.timeRangeOptionText,
                  timeRange === option.id && styles.timeRangeOptionTextActive
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      
      {/* Tab Navigation */}
      <View style={styles.tabContainer}>
        {tabOptions.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tabButton,
              activeTab === tab.id && styles.tabButtonActive
            ]}
            onPress={() => setActiveTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.id ? '#0e4e96' : '#64748B'} 
            />
            <Text
              style={[
                styles.tabButtonText,
                activeTab === tab.id && styles.tabButtonTextActive
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      
      {/* Tab Content */}
      <ScrollView 
        style={styles.scrollContainer}
        contentContainerStyle={styles.contentContainer}
      >
        {renderTabContent()}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerCenter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
  },
  projectHeader: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  projectTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  timeRangeContainer: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  timeRangeLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginRight: 12,
  },
  timeRangeOptions: {
    flexDirection: 'row',
  },
  timeRangeOption: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    marginRight: 8,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  timeRangeOptionActive: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  timeRangeOptionText: {
    fontSize: 12,
    color: '#64748B',
  },
  timeRangeOptionTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  tabContainer: {
    backgroundColor: '#fff',
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabButtonActive: {
    borderBottomColor: '#0e4e96',
  },
  tabButtonText: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  tabButtonTextActive: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  scrollContainer: {
    flex: 1,
    backgroundColor: '#f1f5f9',
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  tabContent: {
    flex: 1,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -4,
    marginBottom: 16,
  },
  statCard: {
    width: '50%',
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0e4e96',
    textAlign: 'center',
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
    textAlign: 'center',
  },
  chartContainer: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 16,
  },
  chart: {
    borderRadius: 8,
    marginVertical: 8,
  },
});